import React, { useState, useEffect } from 'react';
import * as Styles from './FormOpenAccount.styles';
import {
  Button,
  RadioOption,
  Select,
} from '@/pages/common/v1';
import { TextField } from 'gatorcomponents';
import { asaasEnum } from '../../AsaasHandler.enum';

import { isBRServer } from '@/utils/Validators/validation';
import {
  validateCpf,
  validateCnpj,
} from '@/utils/Validators/cpfCnpjValidation';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ENABLE_NEW_VALIDATION_CNPJ_ALPHANUMERIC } from '@/config/GrowthBook/constants';

const AsaasFirstRow = ({
  testId = 'asaas-form-open-account',
  formData,
  onChange,
  onContinue,
  enabledFormOpenAccountContinue,
  cnpjTypes,
  isInsideWizard = false,
}) => {
  const enableNewValidationCnpjAlphanumeric = useFeatureIsOn(ENABLE_NEW_VALIDATION_CNPJ_ALPHANUMERIC);

  const [documentType, setDocumentType] = useState('CPF');
  const [hasErrorDocument, setHasErrorDocument] = useState(false);

  // Sincroniza documentType com props quando o componente monta ou formData muda
  useEffect(() => {
    const propsDocumentType = formData?.fields?.documentType?.value;

    if (propsDocumentType) {
      setDocumentType(propsDocumentType);
    }
  }, [formData?.fields?.documentType?.value]);

  const handleChangeDocument = (e) => {
    const document = e.value;

    if (documentType === 'CPF') {
      validateCpf(document);
      setHasErrorDocument(!validateCpf(document));
    }

    if (documentType === 'CNPJ') {
      const isCNPJAlphanumeric = enableNewValidationCnpjAlphanumeric;
      validateCnpj(document, isCNPJAlphanumeric);
      setHasErrorDocument(!validateCnpj(document, isCNPJAlphanumeric));
    }

    // Atualiza o valor, a validação de erro é tratada externamente
    onChange && onChange({ value: document, name: e.name });
  };

  const handleChangeDocumentInput = (e) => {
    const document = e.target.value;
    onChange && onChange({ value: document, name: e.target.name });
  };

  const handleChange = (e) => {
    onChange && onChange({ value: e.target.value, name: e.target.name });
  };

  const handleToggle = (e, document) => {
    setDocumentType(document);

    // O hook já limpa automaticamente o documentValue quando documentType muda
    onChange && onChange({ value: document, name: e.name });
  };

  const handleChangeSelect = (e) => {
    onChange && onChange({ value: e.value, name: 'companyType' });
  };

  const handleContinue = () => {
    onContinue && onContinue(asaasEnum.formStepper.OPEN_ACCOUNT);
  };

  let maskDocument = '';
  if (isBRServer) {
    if (documentType === 'CNPJ') {
      maskDocument = enableNewValidationCnpjAlphanumeric
        ? 'documentAlphanumeric'
        : 'document';
    }
    if (documentType === 'CPF') {
      maskDocument = 'cpf';
    }
  } else {
    maskDocument = 'noSpecialCharacters';
  }

  return (
    <Styles.FormContent data-testid={testId}>
      <Styles.FormTtitle>{formData.texts[formData.activeStep].title}</Styles.FormTtitle>
      <Styles.FormDescription>{formData.texts[formData.activeStep].description}</Styles.FormDescription>

      <Styles.InputWrapper>
        <TextField
          label={formData.fields.name.placeholder}
          value={formData.fields.name.value}
          name="name"
          onChange={handleChange}
          type="text"
          errorMessage={formData.fields.name.error}
          maxLength={100}
          testId={`${testId}-input-name`}
        />
        <TextField
          label={formData.fields.email.placeholder}
          value={formData.fields.email.value}
          name="email"
          onChange={handleChange}
          type="email"
          errorMessage={formData.fields.email.error}
          maxLength={100}
          testId={`${testId}-input-email`}
        />
      </Styles.InputWrapper>

      <Styles.Question>{formData.texts[formData.activeStep].questionDocument}</Styles.Question>

      <Styles.RadioWrapper>
        <RadioOption
          testId={formData.texts[formData.activeStep].document.cpf}
          title={formData.texts[formData.activeStep].document.cpf}
          basis="1 0 123px"
          onToggle={e => handleToggle(e, 'CPF')}
          checked={formData?.fields?.documentType?.value === 'CPF'}
          name="documentType"
        />
        <RadioOption
          testId={formData.texts[formData.activeStep].document.cnpj}
          title={formData.texts[formData.activeStep].document.cnpj}
          basis="1 0 123px"
          onToggle={e => handleToggle(e, 'CNPJ')}
          checked={formData?.fields?.documentType?.value === 'CNPJ'}
          name="documentType"
        />
      </Styles.RadioWrapper>

      <Styles.InputWrapper>
        <TextField
          label={formData?.fields?.documentType?.value || 'CPF'}
          value={formData?.fields?.documentValue?.value || ''}
          name="documentValue"
          onChange={handleChangeDocumentInput}
          onBlur={handleChangeDocument}
          type="text"
          mask={maskDocument}
          helpText={formData.fields.documentValue.error}
          status={hasErrorDocument ? 'error' : 'default'}
          testId={`${testId}-input-documentValue`}
        />

        <TextField
          label={formData.fields.monthlyIncome && formData.fields.monthlyIncome.placeholder}
          value={formData.fields.monthlyIncome && formData.fields.monthlyIncome.value}
          name="monthlyIncome"
          onChange={handleChange}
          type="text"
          mask="money"
          errorMessage={formData.fields.monthlyIncome && formData.fields.monthlyIncome.error}
          testId={`${testId}-input-monthlyIncome`}
        />

        {documentType === 'CPF' && (
          <TextField
            label={formData.fields.birthday.placeholder}
            value={formData.fields.birthday.value}
            name="birthday"
            onChange={handleChange}
            type="text"
            mask="date"
            maxLength={10}
            errorMessage={formData.fields.birthday.error}
            testId={`${testId}-input-birthday`}
          />
        )}

        {documentType === 'CNPJ' && (
          <Select
            placeholder={formData.fields.companyType.placeholder}
            testId={`${testId}-select-companyType`}
            options={cnpjTypes}
            onChange={handleChangeSelect}
          />
        )}

        {formData.fields.companyType.error && (
          <Styles.LabelError>
            {formData.fields.companyType.error}
          </Styles.LabelError>
        )}
      </Styles.InputWrapper>

      <Styles.ButtonWrapper>
        <Button
          label={formData.texts[formData.activeStep].continueButtonLabel}
          onClick={handleContinue}
          testId={`${testId}-continue`}
          disabled={!enabledFormOpenAccountContinue()}
        />
      </Styles.ButtonWrapper>

      {!isInsideWizard && (
        <Styles.AccessWrapper>
          {formData.texts[formData.activeStep].accessAsaas}
        </Styles.AccessWrapper>
      )}
    </Styles.FormContent>
  );
};

export default AsaasFirstRow;
